import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Book, Search } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Comprehensive glossary for SEO
const FALLBACK_GLOSSARY = [
  { term: 'BFRP', definition: 'Basalt Fiber Reinforced Polymer. A composite material made from basalt rock fibers embedded in a polymer resin matrix. Offers superior tensile strength and corrosion resistance compared to steel.', category: 'Materials' },
  { term: 'GFRP', definition: 'Glass Fiber Reinforced Polymer. A composite material using glass fibers in a polymer matrix. Cost-effective alternative to BFRP with excellent corrosion resistance.', category: 'Materials' },
  { term: 'FRP', definition: 'Fiber Reinforced Polymer. A general term for composite materials combining fiber reinforcement (basalt, glass, carbon) with a polymer resin matrix.', category: 'Materials' },
  { term: 'Pultrusion', definition: 'A continuous manufacturing process for producing FRP composites with constant cross-section. Fibers are pulled through a resin bath and heated die to form solid bars.', category: 'Manufacturing' },
  { term: 'Tensile Strength', definition: 'The maximum stress a material can withstand while being stretched before breaking. FRP rebar typically offers 2× the tensile strength of Grade 60 steel.', category: 'Properties' },
  { term: 'Modulus of Elasticity', definition: 'A measure of material stiffness. FRP has a lower modulus than steel (6-10 Msi vs 29 Msi), making deflection control critical in design.', category: 'Properties' },
  { term: 'ACI 440', definition: 'American Concrete Institute committee and standards covering FRP reinforcement. ACI 440.1R provides design guidelines for FRP-reinforced concrete.', category: 'Standards' },
  { term: 'ASTM D7205', definition: 'Standard test method for tensile properties of fiber reinforced polymer matrix composite bars used in concrete reinforcement.', category: 'Standards' },
  { term: 'Chloride-Induced Corrosion', definition: 'The degradation of steel reinforcement caused by chloride ions from seawater, de-icing salts, or industrial processes penetrating concrete cover.', category: 'Corrosion' },
  { term: 'Concrete Spalling', definition: 'The breaking off of concrete surface layers due to expanding corroded steel reinforcement within. FRP eliminates this failure mode.', category: 'Corrosion' },
  { term: 'Service Life', definition: 'The expected functional lifespan of a structure or component. FRP-reinforced structures can achieve 100+ year service life vs 15-25 years for steel in corrosive environments.', category: 'Design' },
  { term: 'Cover Depth', definition: 'The thickness of concrete between the reinforcement and the exposed surface. FRP allows reduced cover depths due to corrosion immunity.', category: 'Design' },
  { term: 'Deflection-Governed Design', definition: 'Design approach where serviceability limits (deflection, crack width) control rather than ultimate strength. Common with FRP due to lower modulus.', category: 'Design' },
  { term: 'Geogrid', definition: 'A geosynthetic material with open grid structure used for soil reinforcement, slope stabilization, and pavement applications.', category: 'Products' },
  { term: 'Basalt Fiber', definition: 'Continuous fiber produced from volcanic basalt rock. Offers excellent mechanical properties, chemical resistance, and thermal stability.', category: 'Materials' },
  { term: 'Epoxy Resin', definition: 'A thermosetting polymer used as the matrix material in FRP composites. Provides excellent adhesion, chemical resistance, and mechanical properties.', category: 'Materials' },
  { term: 'Vinyl Ester Resin', definition: 'A hybrid resin combining properties of epoxy and polyester. Often used in GFRP for good corrosion resistance at lower cost than epoxy.', category: 'Materials' },
  { term: 'Alkaline Resistance', definition: 'The ability to withstand degradation in high-pH environments like concrete. Basalt fibers offer superior alkaline resistance vs E-glass.', category: 'Properties' },
  { term: 'Thermal Conductivity', definition: 'The rate at which heat passes through a material. FRP has ~100× lower thermal conductivity than steel, reducing thermal bridging.', category: 'Properties' },
  { term: 'Non-Magnetic', definition: 'Property of not being affected by magnetic fields. Critical for MRI facilities, sensitive electronics, and military applications.', category: 'Properties' },
];

export default function GlossaryPage() {
  const [glossary, setGlossary] = useState(FALLBACK_GLOSSARY);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    const fetchGlossary = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/glossary`);
        if (response.data.terms?.length > 0) {
          setGlossary(response.data.terms);
        }
      } catch (error) {
        console.log('Using fallback glossary');
      }
    };
    fetchGlossary();
  }, []);

  const categories = ['All', ...new Set(glossary.map(item => item.category))];

  const filteredGlossary = glossary
    .filter(item => 
      (activeCategory === 'All' || item.category === activeCategory) &&
      (item.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
       item.definition.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => a.term.localeCompare(b.term));

  return (
    <div className="bg-charcoal min-h-screen pt-20" data-testid="glossary-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 border-b border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-4 mb-4">
              <Book size={24} className="text-gold" />
              <span className="tech-label">//TERMINOLOGY</span>
            </div>
            <h1 className="heading-display text-stone text-5xl md:text-6xl mb-6">
              Industry <span className="text-gold">Glossary</span>
            </h1>
            <p className="text-stone/50 max-w-2xl text-lg">
              Definitions of key terms in composite reinforcement, FRP technology, 
              and construction materials science.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="py-8 border-b border-border-dark sticky top-20 bg-charcoal z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone/40" />
              <input
                type="text"
                placeholder="Search terms..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-12"
                data-testid="glossary-search"
              />
            </div>
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-3 py-2 text-mono text-xs transition-all ${
                    activeCategory === category
                      ? 'bg-gold text-charcoal'
                      : 'border border-border-dark text-stone/60 hover:border-gold/30'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Glossary List */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="space-y-6">
            {filteredGlossary.map((item, index) => (
              <motion.div
                key={item.term}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.02 }}
                className="p-6 border border-border-dark hover:border-gold/20 transition-colors"
                data-testid={`glossary-term-${item.term.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start justify-between gap-4 mb-2">
                  <h2 className="heading-section text-gold text-xl">{item.term}</h2>
                  <span className="text-mono text-xs text-stone/40 shrink-0">{item.category}</span>
                </div>
                <p className="text-stone/70 leading-relaxed">{item.definition}</p>
              </motion.div>
            ))}
          </div>

          {filteredGlossary.length === 0 && (
            <div className="text-center py-12">
              <p className="text-stone/40">No terms found matching "{searchTerm}"</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
